// Config
const API_URL = process.env.REACT_APP_API_URL || '/.netlify/functions/api'

const BASE_API_URL = `${API_URL}`

// Content
export const GET_USER_COMPANY = `${BASE_API_URL}/user/company`

export const GET_COMPANY_TERMS = () => `${BASE_API_URL}/user/company/terms`

export const GET_TODOS = (memberId: string) =>
  `${BASE_API_URL}/members/${memberId}/todos`

export const GET_REQUESTS = (memberId: string) =>
  `${BASE_API_URL}/user/company/members/${memberId}/requests`

export const GET_NOTIFICATIONS = (limit?: number) =>
  `${BASE_API_URL}/notifications${limit ? `?limit=${limit}` : ''}`

export const MARK_NOTIFICATIONS_READ = `${BASE_API_URL}/notifications/read`

export const SYNC_RELEASE_NOTES = `${BASE_API_URL}/user/company/release-notes/sync`

export const MARK_RELEASE_NOTE_OPENED = `${BASE_API_URL}/user/company/release-notes/opened`

export const GET_PDPS = (termNumber: number) =>
  `${BASE_API_URL}/pdps/${termNumber}`

export const SET_PDP_STATE = (memberId: string, state: string) =>
  `${BASE_API_URL}/members/${memberId}/pdp/state/${state}`

export const IMPROVE_PDP = (memberId: string) =>
  `${BASE_API_URL}/members/${memberId}/pdp/improve`

export const MODIFY_PDP_GOAL = (memberId: string, goalId: string) =>
  `${BASE_API_URL}/members/${memberId}/pdp/goals/${goalId}/submissions`

export const AUTH_USER_DETAILS = (slug?: string) =>
  `${BASE_API_URL}/users/auth${slug ? `?selectedCompanySlug=${slug}` : ``}`

export const USER_LOGIN = `${BASE_API_URL}/users/auth/google`
export const MICROSOFT_LOGIN = `${BASE_API_URL}/users/auth/microsoft`

export const SAVE_USER = () => `${BASE_API_URL}/user/company/members`

export const EDIT_USER = (memberId: string) =>
  `${BASE_API_URL}/members/${memberId}`

export const CHANGE_ADMIN_STATE = (memberId: string) =>
  `${BASE_API_URL}/members/${memberId}/admin`

export const DISABLE_USER = (memberId: string) =>
  `${BASE_API_URL}/members/${memberId}/disable`

export const ADD_OR_UPDATE_SKILL = () => `${BASE_API_URL}/user/company/skills`

export const UPDATE_JOB_PROPERTIES = () =>
  `${BASE_API_URL}/user/company/settings/job-properties`

export const ADD_OR_UPDATE_CULTURE_VALUE = () =>
  `${BASE_API_URL}/user/company/culture/values`

export const REORDER_CULTURE_VALUES = () =>
  `${BASE_API_URL}/user/company/culture/values/reorder`

export const ADD_OR_UPDATE_CULTURE_RANK = () =>
  `${BASE_API_URL}/user/company/culture/ranks`

export const REORDER_CULTURE_RANKS = () =>
  `${BASE_API_URL}/user/company/culture/ranks/reorder`

export const INITIALIZE_CULTURE_RANKS = () =>
  `${BASE_API_URL}/user/company/culture/ranks/initialize`

// Bulk import of a pasted culture matrix (support-only). `dryRun` returns the
// reconciliation plan; the real call applies it in one atomic save.
export const IMPORT_CULTURE_VALUES = () =>
  `${BASE_API_URL}/user/company/culture/values/import`

// Bulk import of a pasted jobs table (support-only). `analyze` returns the
// reconciliation plan; `apply` commits the admin's decisions in one atomic save.
export const IMPORT_JOBS = () => `${BASE_API_URL}/user/company/jobs/import`

// Bulk import of a pasted skills table (support-only). `analyze` returns the
// reconciliation preview; `apply` commits it in one atomic save.
export const IMPORT_SKILLS = () => `${BASE_API_URL}/user/company/skills/import`

export const GENERATE_CULTURE_VALUES = () =>
  `${BASE_API_URL}/ai/culture/values/generate`

export const GENERATE_MISSION_STATEMENT = () =>
  `${BASE_API_URL}/ai/mission/generate`

export const GENERATE_TONE_OF_VOICE = () => `${BASE_API_URL}/ai/tone/generate`

// Career-framework generator (domains + growth paths + levels + missions) and
// the scoped skills generator. Both start an async job; APPLY persists the
// reviewed proposal in one company save.
export const GENERATE_FRAMEWORK = () => `${BASE_API_URL}/ai/framework/generate`
export const APPLY_AI_FRAMEWORK = () => `${BASE_API_URL}/ai/framework/apply`
export const GENERATE_SKILLS = () => `${BASE_API_URL}/ai/skills/generate`
export const APPLY_AI_SKILLS = () => `${BASE_API_URL}/ai/skills/apply`

// Generic status poll for any async AI generation job (culture values, mission,
// tone of voice, framework, skills). Job `kind` decides the shape of `result`.
export const AI_JOB = (jobId: string) => `${BASE_API_URL}/ai/job/${jobId}`

export const DELETE_CULTURE_RANK = () =>
  `${BASE_API_URL}/user/company/culture/ranks/delete`

export const GET_PDP_BY_USER = (
  memberId: string,
  termNumber: number | 'latest',
  evaluationId?: string
) =>
  `${BASE_API_URL}/members/${memberId}/term/${termNumber}/pdp${
    evaluationId ? '?evaluationId=' + evaluationId : ''
  }`

export const EDIT_COMPANY_NAME = `${BASE_API_URL}/user/company/properties/name`

export const COMPANY_COLORS = `${BASE_API_URL}/user/company/properties/colors`

export const COMPANY_ASSETS = `${BASE_API_URL}/user/company/properties/assets`

export const COMPANY_UPLOAD_PRESIGN = `${BASE_API_URL}/user/company/uploads/presign`

export const COMPANY_FILES = `${BASE_API_URL}/user/company/files`

export const COMPANY_FILE = (fileId: string) =>
  `${BASE_API_URL}/user/company/files/${fileId}`

export const COMPANY_MEDIA = `${BASE_API_URL}/user/company/media`

export const COMPANY_MEDIA_ITEM = (mediaId: string) =>
  `${BASE_API_URL}/user/company/media/${mediaId}`

export const COMPANY_PDF_SETTINGS = `${BASE_API_URL}/user/company/pdf-settings`

export const MAIL_PREFERENCES = `${BASE_API_URL}/user/company/mail-preferences`

export const COMPANY_MAIL_SETTINGS = `${BASE_API_URL}/user/company/mail-settings`

export const SET_COMPANY_MODULE_ACCESS = `${BASE_API_URL}/support/company/module-access`

export const GOOGLE_FONTS = `${BASE_API_URL}/user/company/google-fonts`

export const UPDATE_AI_CONTEXT = `${BASE_API_URL}/support/company/ai-context`

export const REFRESH_TOKEN = `${BASE_API_URL}/users/auth/refresh`

export const ADD_TEAM = () => `${BASE_API_URL}/user/company/teams`

export const EDIT_TEAM = (teamId: string) =>
  `${BASE_API_URL}/user/company/teams/${teamId}`

export const GET_COMPANY_RITUALS_DATA = () =>
  `${BASE_API_URL}/user/company/rituals`

export const DELETE_TODO = (memberId: string, todoId: string) =>
  `${BASE_API_URL}/members/${memberId}/todos/${todoId}`

export const ADD_NEW_GOAL = (memberId: string) =>
  `${BASE_API_URL}/members/${memberId}/pdp/goals`

export const SUBMIT_GOAL_MESSAGE = (memberId: string, goalId: string) =>
  `${BASE_API_URL}/members/${memberId}/pdp/goals/${goalId}/messages`

export const MARK_GOAL_MESSAGE_AS_OPENED = (pdpId: string, goalId: string) =>
  `${BASE_API_URL}/pdps/${pdpId}/goals/${goalId}/messages/open`

export const SUBMIT_CUSTOM_LEAD = (memberId: string) =>
  `${BASE_API_URL}/members/${memberId}/leads`

export const UPDATE_MEMBER_BUDDIES = (memberId: string) =>
  `${BASE_API_URL}/members/${memberId}/buddies`

export const SET_GOAL_APPROVED_STATUS = (memberId: string, goalId: string) =>
  `${BASE_API_URL}/members/${memberId}/pdp/goals/${goalId}/approve`

export const REMOVE_TEAM = (teamId: string) =>
  `${BASE_API_URL}/user/company/teams/${teamId}`

export const ADD_DEPARTMENT = () => `${BASE_API_URL}/user/company/departments`

export const EDIT_DEPARTMENT = (departmentId: string) =>
  `${BASE_API_URL}/user/company/departments/${departmentId}`

export const REMOVE_DEPARTMENT = (departmentId: string) =>
  `${BASE_API_URL}/user/company/departments/${departmentId}`

export const DELETE_SKILL_FROM_COMPANY = () =>
  `${BASE_API_URL}/user/company/skills/delete`

export const DELETE_GOAL_FROM_PDP = (memberId: string, goalId: string) =>
  `${BASE_API_URL}/members/${memberId}/pdp/goals/${goalId}/delete`

export const PIN_GOAL = (memberId: string, goalId: string) =>
  `${BASE_API_URL}/members/${memberId}/pdp/goals/${goalId}/pin`

export const SPECIFY_CLOSE_MEMBERS = (memberId: string, termNumber: number) =>
  `${BASE_API_URL}/members/${memberId}/close-members/${termNumber}`

export const GET_COMPANY_FEEDBACK_AGGREGATIONS = () =>
  `${BASE_API_URL}/user/company/feedbacks/numbers`

export const GET_AUTH_USER_FEEDBACK_DETAILS = () =>
  `${BASE_API_URL}/user/auth/feedbacks/statistics`

export const GET_COMPANY_METADATA = (termNumber: number) =>
  `${BASE_API_URL}/user/company/metadata/${termNumber}`

export const GET_SAVED_FEEDBACK = (feedbackId: string) =>
  `${BASE_API_URL}/feedbacks/${feedbackId}`

export const SUBMIT_PARTIAL_FEEDBACK_FORM = (feedbackId: string) =>
  `${BASE_API_URL}/feedbacks/${feedbackId}/partialfeedback`

export const SUBMIT_FEEDBACK_FORM = (feedbackId: string) =>
  `${BASE_API_URL}/feedbacks/${feedbackId}`

export const DELETE_FEEDBACK = (feedbackId: string) =>
  `${BASE_API_URL}/feedbacks/${feedbackId}`

export const GET_MEMBER_FEEDBACK = (memberId: string) =>
  `${BASE_API_URL}/members/${memberId}/feedbacks`

export const GET_ALL_MEMBER_FEEDBACK = (memberId: string) =>
  `${BASE_API_URL}/members/${memberId}/feedbacks/all`

export const GET_MEMBER_FEEDBACK_HIGHLIGHTS = (memberId: string) =>
  `${BASE_API_URL}/members/${memberId}/feedbacks/highlights`

export const GET_RECENT_FEEDBACK = () =>
  `${BASE_API_URL}/feedbacks/overview/recent`

export const GET_MEMBER_COMPETENCY_STATISTICS = (memberId: string) =>
  `${BASE_API_URL}/members/${memberId}/feedbacks/competencies/statistics`

export const GET_SAVED_EVALUATION = (evaluationId: string) =>
  `${BASE_API_URL}/evaluations/${evaluationId}`

export const SKIP_EVALUATION = (evaluationId: string) =>
  `${BASE_API_URL}/evaluations/${evaluationId}`

export const GET_MEMBER_EVALUATIONS = (memberId: string) =>
  `${BASE_API_URL}/members/${memberId}/evaluations`

export const GET_MEMBER_OUTGOING_EVALUATIONS = (termNumber: string) =>
  `${BASE_API_URL}/evaluations/outgoing/${termNumber}`

export const SAVE_PARTIAL_SKILL_EVALUATION = (evaluationId: string) =>
  `${BASE_API_URL}/evaluations/${evaluationId}/skills`

export const SAVE_PARTIAL_GOAL_EVALUATION = (evaluationId: string) =>
  `${BASE_API_URL}/evaluations/${evaluationId}/goals`

export const SAVE_PARTIAL_CULTURE_EVALUATION = (evaluationId: string) =>
  `${BASE_API_URL}/evaluations/${evaluationId}/culture`

export const UPDATE_EVALUATION_SUBMITTED_PARTS = (evaluationId: string) =>
  `${BASE_API_URL}/evaluations/${evaluationId}/status`

export const ADD_EVALUATION_ASSESSORS = (evaluationId: string) =>
  `${BASE_API_URL}/evaluations/${evaluationId}/assessors`

export const REMOVE_EVALUATION_ASSESSORS = (evaluationId: string) =>
  `${BASE_API_URL}/evaluations/${evaluationId}/assessors/remove`

export const REOPEN_EVALUATION_ASSESSMENT = (evaluationId: string) =>
  `${BASE_API_URL}/evaluations/${evaluationId}/assessment/reopen`

export const FINALIZE_EVALUATION_PLANNING = (termNumber: string) =>
  `${BASE_API_URL}/evaluations/finalize-planning/${termNumber}`

export const CLOSE_EVALUATION = (termNumber: string) =>
  `${BASE_API_URL}/evaluations/close/${termNumber}`

export const EDIT_EVALUATION_NAME = (termNumber: string) =>
  `${BASE_API_URL}/evaluations/name/${termNumber}`

export const EDIT_EVALUATION_HIDE_ASSESSMENTS = (termNumber: string) =>
  `${BASE_API_URL}/evaluations/hide/${termNumber}`

export const EDIT_EVALUATIONS_SETTINGS = `${BASE_API_URL}/user/company/evaluations-settings`

export const GET_MEMBER_COLLECTIONS = (memberId: string) =>
  `${BASE_API_URL}/members/${memberId}/collections`

export const GET_MEMBER_SCORE_TREND = (memberId: string) =>
  `${BASE_API_URL}/members/${memberId}/score-trend`

export const GET_SINGLE_COLLECTION = (
  collectionId: string,
  asMember?: boolean,
  withAchievements = true
) => {
  const params = new URLSearchParams()
  if (asMember) params.set('asMember', '1')
  // Skip the (history-wide) achievement fan-out when the caller only needs the
  // collection for deltas — see loadSingleCollectionByMember / previous-term load.
  if (!withAchievements) params.set('withAchievements', '0')
  const query = params.toString()
  return `${BASE_API_URL}/collections/${collectionId}${
    query ? `?${query}` : ''
  }`
}

export const IMAGE_PROXY = (url: string) =>
  `${BASE_API_URL}/media/image-proxy?url=${encodeURIComponent(url)}`

export const SUBMIT_COLLECTION_NOTE = (collectionId: string) =>
  `${BASE_API_URL}/collections/${collectionId}/notes`

// Single note resource — DELETE and UPDATE hit the same URL, only the verb differs.
export const COLLECTION_NOTE_ITEM = (collectionId: string, noteId: string) =>
  `${BASE_API_URL}/collections/${collectionId}/notes/${noteId}`

export const DELETE_COLLECTION_NOTE = COLLECTION_NOTE_ITEM
export const UPDATE_COLLECTION_NOTE = COLLECTION_NOTE_ITEM

export const WRAP_UP_COLLECTION = (collectionId: string) =>
  `${BASE_API_URL}/collections/${collectionId}/wrap-up`

export const GET_COMPANY_TERM_EVALUATION_STATE = (termId: string) =>
  `${BASE_API_URL}/user/company/evaluations/${termId}`

export const GET_EVALUATIONS_FOR_LEAD = (teamId: string, termId: string) =>
  `${BASE_API_URL}/teams/${teamId}/evaluations/${termId}`

export const GET_EXTERNAL_FEEDBACK = (externalFeedbackId: string) =>
  `${BASE_API_URL}/external-feedback/${externalFeedbackId}`

export const SUBMIT_EXTERNAL_FEEDBACK = (externalFeedbackId: string) =>
  `${BASE_API_URL}/external-feedback/${externalFeedbackId}`

export const REQUEST_INTERNAL_FEEDBACK = (memberId: string) =>
  `${BASE_API_URL}/members/${memberId}/feedbacks/request-internal`

export const REQUEST_EXTERNAL_FEEDBACK = (memberId: string) =>
  `${BASE_API_URL}/members/${memberId}/feedbacks/request-external`

export const REQUEST_GOALS_REVIEW = (pdpId: string) =>
  `${BASE_API_URL}/pdps/${pdpId}/reviewers`

export const CHANGE_PDP_REVIEW_STATUS = (pdpId: string) =>
  `${BASE_API_URL}/pdps/${pdpId}/review/status`

export const GET_PREVIOUS_TERM_PDP = (pdpId: string) =>
  `${BASE_API_URL}/pdps/${pdpId}/previous-pdp`

export const SUPPORT_EMAIL = (memberId?: string) =>
  `${BASE_API_URL}/support/${memberId}`

export const DELETE_LEVEL = () => `${BASE_API_URL}/company/levels/delete`

export const CREATE_ONBOARDING_TEMPLATE = `${BASE_API_URL}/onboarding-templates`

export const EDIT_ONBOARDING_TEMPLATE_NAME = (onboardingId: string) =>
  `${BASE_API_URL}/onboarding-templates/${onboardingId}`

export const GET_COMPANY_ONBOARDING_TEMPLATES_OVERVIEW = `${BASE_API_URL}/onboarding-templates`

export const ADD_ONBOARDING_TEMPLATE_CHAPTER = (onboardingId: string) =>
  `${BASE_API_URL}/onboarding-templates/${onboardingId}/chapters`

export const EDIT_ONBOARDING_TEMPLATE_CHAPTER_ACTIVITY_ORDER = (
  onboardingId: string,
  chapterId: string
) =>
  `${BASE_API_URL}/onboarding-templates/${onboardingId}/chapters/${chapterId}`

export const REMOVE_ONBOARDING_TEMPLATE_CHAPTER = (
  onboardingId: string,
  chapterId: string
) =>
  `${BASE_API_URL}/onboarding-templates/${onboardingId}/chapters/${chapterId}`

export const ADD_ONBOARDING_TEMPLATE_CHAPTER_ACTIVITY = (
  onboardingId: string,
  chapterId: string
) =>
  `${BASE_API_URL}/onboarding-templates/${onboardingId}/chapters/${chapterId}/activities`

export const REMOVE_ONBOARDING_TEMPLATE_CHAPTER_ACTIVITY = (
  onboardingId: string,
  chapterId: string,
  activityId: string
) =>
  `${BASE_API_URL}/onboarding-templates/${onboardingId}/chapters/${chapterId}/activities/${activityId}`

export const EDIT_ONBOARDING_TEMPLATE_CHAPTER_ACTIVITY = (
  onboardingId: string,
  chapterId: string,
  activityId: string
) =>
  `${BASE_API_URL}/onboarding-templates/${onboardingId}/chapters/${chapterId}/activities/${activityId}`

export const GET_ONBOARDING_TEMPLATE_CHAPTER_ACTIVITY = (
  onboardingId: string,
  activityId: string
) =>
  `${BASE_API_URL}/onboarding-templates/${onboardingId}/activities/${activityId}`

export const REMOVE_ONBOARDING_TEMPLATE = (onboardingId: string) =>
  `${BASE_API_URL}/onboarding-templates/${onboardingId}`

export const GET_ONBOARDING_TEMPLATE = (onboardingId: string) =>
  `${BASE_API_URL}/onboarding-templates/${onboardingId}`

export const DUPLICATE_ONBOARDING_TEMPLATE = (onboardingId: string) =>
  `${BASE_API_URL}/onboarding-templates/${onboardingId}/duplicate`

export const SEND_SUGGEST_REQUESTS = (termNumber: number) =>
  `${BASE_API_URL}/company/suggest-requests/${termNumber}`

export const GET_INSIGHTS_STATISTICS = (termNumber: number) =>
  `${BASE_API_URL}/insights/statistics/${termNumber}`

export const GET_INSIGHTS_TRENDS = (termNumber: number) =>
  `${BASE_API_URL}/insights/trends/term/${termNumber}`

export const CREATE_ONBOARDING_INSTANCE = `${BASE_API_URL}/onboarding-instances`

export const DELETE_ONBOARDING_INSTANCE = (onboardingInstanceId: string) =>
  `${BASE_API_URL}/onboarding-instances/${onboardingInstanceId}`

export const GET_COMPANY_ONBOARDING_INSTANCES = (membersNumber?: number) =>
  `${BASE_API_URL}/onboarding-instances${
    membersNumber ? `?membersNumber=${membersNumber}` : ``
  }`

export const GET_ONBOARDING_INSTANCE = (instanceId: string) =>
  `${BASE_API_URL}/onboarding-instances/${instanceId}`

export const GET_ONBOARDING_INSTANCE_HIGHLIGHTS = (instanceId: string) =>
  `${BASE_API_URL}/onboarding-instances/${instanceId}/highlights`

export const GET_ONBOARDING_INSTANCE_PREVIEW = (
  instanceId: string,
  memberId: string
) => `${BASE_API_URL}/onboarding-instances/preview/${memberId}/${instanceId}`

export const UNLOCK_ONBOARDING_INSTANCE_CHAPTER = (
  instanceId: string,
  chapterId: string
) =>
  `${BASE_API_URL}/onboarding-instances/${instanceId}/chapters/${chapterId}/unlock`

export const COMPLETE_ONBOARDING_INSTANCE_STORYLINE = (instanceId: string) =>
  `${BASE_API_URL}/onboarding-instances/${instanceId}/storyline/complete`

export const CHANGE_ONBOARDING_ACTIVITY_STATUS = (
  instanceId: string,
  activityId: string
) =>
  `${BASE_API_URL}/onboarding-instances/${instanceId}/activities/${activityId}/status`

export const UPDATE_COMPANY_LIVE_MANIFESTO = (companySlug: string) =>
  `${BASE_API_URL}/company/manifesto/${companySlug}/update`

export const GET_COMPANY_LIVE_MANIFESTO = (companySlug: string) =>
  `${BASE_API_URL}/company/manifesto/${companySlug}`

export const GET_PUBLIC_MANIFESTO_PAGE = (companySlug: string, page: string) =>
  `${BASE_API_URL}/company/manifesto/${companySlug}/${page}`

export const UNLOCK_PUBLIC_MANIFESTO = (companySlug: string) =>
  `${BASE_API_URL}/company/manifesto/${companySlug}/unlock`

export const GET_PUBLIC_JOB_PROFILE = (
  companySlug: string,
  jobLevelId: string
) => `${BASE_API_URL}/company/manifesto/${companySlug}/job/${jobLevelId}`

export const CREATE_JOURNAL = (memberId: string) =>
  `${BASE_API_URL}/members/${memberId}/journals/create`

export const UPDATE_JOURNAL_AS_OWNER = (memberId: string) =>
  `${BASE_API_URL}/members/${memberId}/journals/updateAsOwner`

export const GET_JOURNALS = (memberId: string) =>
  `${BASE_API_URL}/members/${memberId}/journals/get`

export const UPDATE_JOURNAL_PARTICIPANTS = (memberId: string) =>
  `${BASE_API_URL}/members/${memberId}/journals/participants`

export const UPDATE_JOURNAL_PREFERENCES = (memberId: string) =>
  `${BASE_API_URL}/members/${memberId}/journals/update`

export const UPDATE_JOURNAL_DATE_OPENED = (memberId: string) =>
  `${BASE_API_URL}/members/${memberId}/journals/dateOpened`

export const ADD_JOURNAL_ENTRY = (memberId: string) =>
  `${BASE_API_URL}/members/${memberId}/journals/entry`

export const DELETE_JOURNAL_ENTRY = (memberId: string) =>
  `${BASE_API_URL}/members/${memberId}/journals/entry/delete`

export const DELETE_JOURNAL = (memberId: string) =>
  `${BASE_API_URL}/members/${memberId}/journals/delete`

export const DELETE_ALL_MEMBER_DATA = (memberId: string) =>
  `${BASE_API_URL}/members/${memberId}/delete`

export const UPDATE_JOB_LEVEL_SKILLS = (jobLevelId: string) =>
  `${BASE_API_URL}/company/jobs/level/${jobLevelId}/skills`

export const CHANGE_JOB_LEVEL_SKILLS = () =>
  `${BASE_API_URL}/company/jobs/skills/membership`

export const UPDATE_JOB_LEVEL_RANK = (jobLevelId: string) =>
  `${BASE_API_URL}/company/jobs/level/${jobLevelId}/rank`

export const UPDATE_JOB_LEVEL_DETAILS = (jobLevelId: string) =>
  `${BASE_API_URL}/company/jobs/level/${jobLevelId}`

export const DELETE_JOB_LEVEL = (jobLevelId: string) =>
  `${BASE_API_URL}/company/jobs/level/${jobLevelId}/delete`

export const MOVE_JOB_LEVEL = (jobLevelId: string) =>
  `${BASE_API_URL}/company/jobs/level/${jobLevelId}/move`

export const REORDER_JOB_LEVELS = (jobId: string) =>
  `${BASE_API_URL}/company/jobs/${jobId}/levels/reorder`

export const REORDER_JOB_LEVEL_SKILLS = (jobLevelId: string) =>
  `${BASE_API_URL}/company/jobs/level/${jobLevelId}/skills/reorder`

export const ADD_NEW_JOB_LEVEL = (jobId: string) =>
  `${BASE_API_URL}/company/jobs/${jobId}/levels/new`

export const ADD_NEW_JOB = () => `${BASE_API_URL}/company/jobs/new`

export const UPDATE_JOB = (jobId: string) =>
  `${BASE_API_URL}/company/jobs/${jobId}`

export const DELETE_JOB = (jobId: string) =>
  `${BASE_API_URL}/company/jobs/${jobId}/delete`

// The target database is resolved server-side from the deploy's DB_NAME — it is
// deliberately not a client-supplied parameter, so a caller cannot aim the script
// at a database the deploy was never meant to touch.
export const EXECUTE_SCRIPT = (
  scriptName: string,
  withManifesto: boolean,
  withJournal: boolean,
  withOnboardingTemplate: boolean,
  overrideCompany: boolean,
  isTest: boolean,
  withSeedData: boolean
) =>
  `${BASE_API_URL}/scripts/${scriptName}?isTest=${isTest}&withManifesto=${withManifesto}&withJournal=${withJournal}&withOnboardingTemplate=${withOnboardingTemplate}&overrideCompany=${overrideCompany}&withSeedData=${withSeedData}`

// Which database this deploy writes to, needed before a sheet exists to dry-run.
export const GET_SCRIPT_TARGET = () => `${BASE_API_URL}/scripts/target`

export const ADVANCE_TERM_STEP = `${BASE_API_URL}/company/terms/next-step`

export const CREATE_COMPANY = () => `${BASE_API_URL}/scripts/createCompany`

// Seeds the company the support user is working in, on the deploy's own database
export const SEED_DEMO_DATA = (count: number, mode?: string) =>
  `${BASE_API_URL}/scripts/seedDemoData?count=${count}${
    mode ? `&mode=${mode}` : ''
  }`

export const GET_MEMBERS_TO_BE_DELETED = () =>
  `${BASE_API_URL}/members/to-delete`

export const INTEGRATION_STATUS = () => `${BASE_API_URL}/integrations/status`

export const INTEGRATION_CONNECT_NMBRS = () =>
  `${BASE_API_URL}/integrations/nmbrs/connect`

export const INTEGRATION_COMPLETE_NMBRS = () =>
  `${BASE_API_URL}/integrations/nmbrs/complete`

export const INTEGRATION_CONNECT_AFAS = () =>
  `${BASE_API_URL}/integrations/afas/connect`

export const INTEGRATION_PREVIEW = () => `${BASE_API_URL}/integrations/preview`

export const INTEGRATION_ENABLE = () => `${BASE_API_URL}/integrations/enable`

export const INTEGRATION_JOBS = () => `${BASE_API_URL}/integrations/jobs`

export const INTEGRATION_JOBS_APPLY = () =>
  `${BASE_API_URL}/integrations/jobs/apply`

export const INTEGRATION_COMPANIES = () =>
  `${BASE_API_URL}/integrations/companies`

export const INTEGRATION_APPROVE_ADDS = () =>
  `${BASE_API_URL}/integrations/approve-adds`

export const INTEGRATION_DISCONNECT = () =>
  `${BASE_API_URL}/integrations/disconnect`
